import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { closeAIConversation, aiChatSelectors, minimizeChat, openChat } from 'app-components/flux/ai-chat';
import { DraggablePopper } from 'components/DraggablePopper';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useRef, useState, type JSX } from 'react';
import { tracker } from 'app-components/utils/core';
import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import { useChatDock, getNextChatViewMode } from './ChatDockContext';
import type { ChatViewMode } from './ChatDockContext';

import ChatIcon from '@mui/icons-material/Chat';
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Fab from '@mui/material/Fab';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';

import AILogoSimpleIcon from 'assets/icons/quicken-ai/ai-logo-simple.inline.svg';
import { AI_LOGO_SIMPLE_GLYPH_SIZE } from 'assets/icons/quicken-ai/aiLogoSimple';
import AIChat from 'components/AIChat';
import { ChatSessionProvider } from 'components/ChatLauncher/chatSession';
import SimplifiAiAssistant from 'containers/SimplifiAiAssistant';
import { randomGreeting } from 'components/AIChat/utils';
import { useAiChatFirstOpenPendoGuide } from './useAiChatFirstOpenPendoGuide';
import { useChatDisclaimerGate } from './useChatDisclaimerGate';

const isDev =
  window?.location?.hostname !== 'simplifi.quicken.com' && window?.location?.hostname !== 'app.simplifimoney.com';

interface ChatLauncherProps {
  optBotOffset?: number;
  optRightOffset?: number;
  zIndex?: number;
}

const MinimizeWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMinimized',
})<{ isMinimized: boolean }>(({ theme, isMinimized }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  background: isMinimized ? theme.palette.ai.background : 'transparent',
  borderRadius: isMinimized ? theme.spacing(2.5) : theme.spacing(5),
  boxShadow: isMinimized ? theme.shadows[13] : theme.shadows[1],
  padding: isMinimized ? theme.spacing(0.5) : theme.spacing(0.5),
  transition:
    'background 0.4s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  backdropFilter: 'blur(8px)',
  '&:hover': isMinimized
    ? {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[15],
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    : { transform: 'scale(0.90)', transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)' },
}));

const MinimizedChatTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  minWidth: 120,
  maxWidth: 200,
  height: 40,
  cursor: 'pointer',
  transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const ChatLauncherContent = ({
  optBotOffset = 20, // Default value if not provided
  optRightOffset = 20, // Default value if not provided
  zIndex = 1000, // Default value if not provided
}: ChatLauncherProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  useAiChatFirstOpenPendoGuide();
  const { requestOpenChat } = useChatDisclaimerGate();
  const launcherStyles: SxProps<Theme> = {
    position: 'fixed',
    bottom: optBotOffset,
    right: optRightOffset,
    zIndex: zIndex,
    alignItems: 'center',
  };

  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const chatDragHandleRef = useRef<HTMLDivElement | null>(null);
  const { viewMode, setViewMode } = useChatDock();

  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const chatUIState = useAppSelector(aiChatSelectors.getChatUIState);
  const open = chatUIState !== 'closed';
  const isMinimized = chatUIState === 'minimized';
  const isChatOpen = chatUIState === 'open';

  // Always show "Resume Chat" in minimized view
  const chatTitle = 'Resume Chat';

  // Store greeting in ChatLauncher so it persists across dock/undock (when AIChat remounts)
  const greetingRef = useRef<string | null>(null);
  if (greetingRef.current === null) {
    greetingRef.current = randomGreeting();
  }
  const greeting = greetingRef.current;

  const ffAiChat = useAppSelector((reduxState) => featureFlagsSelectors.getFeatureFlags(reduxState).get('aiChat'));
  const ffAiChat2 = useAppSelector((reduxState) => featureFlagsSelectors.getFeatureFlags(reduxState).get('aiChatV2'));
  const [gsfScreen] = usePreferenceV2('gsfScreen');
  const [AIEnabled] = usePreferenceV2('aiInsightsOptIn');
  const completedOnboarding = gsfScreen === 'completed_gsf';

  // Detect platform for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';
  const shiftKey = isMac ? '⇧' : 'Shift';

  // Keyboard shortcut handlers: Command+Shift+O (open/minimize) and Command+. (dock/undock)
  React.useEffect(() => {
    const platformIsMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      // Check if chat feature is enabled
      if (!ffAiChat2 || !AIEnabled || !completedOnboarding) {
        return;
      }

      // Don't trigger if user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('input, textarea, [contenteditable="true"]');

      if (isInputField) {
        return;
      }

      const modifierKey = platformIsMac ? event.metaKey : event.ctrlKey;

      // Check for Command+Shift+O (Mac) or Ctrl+Shift+O (Windows/Linux) - Open/Minimize
      const isOpenShortcut = modifierKey && event.shiftKey && event.key.toLowerCase() === 'o';

      // Check for Command+. (Mac) or Ctrl+. (Windows/Linux) - Dock/Undock
      const isDockShortcut = modifierKey && !event.shiftKey && !event.altKey && event.key === '.';

      if (isOpenShortcut) {
        event.preventDefault();
        event.stopPropagation();

        tracker.track(trackingEvents.AIChatClick, { variant: 'keyboard_shortcut' });

        if (isMinimized) {
          // If minimized, restore it
          requestOpenChat(() => dispatch(openChat()));
        } else if (isChatOpen) {
          dispatch(minimizeChat());
        } else {
          requestOpenChat(() => dispatch(openChat()));
        }
      } else if (isDockShortcut) {
        event.preventDefault();
        event.stopPropagation();

        const nextViewMode = getNextChatViewMode(viewMode);
        setViewMode(nextViewMode);
        if (nextViewMode !== 'floating') {
          requestOpenChat(() => dispatch(openChat()));
        }
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      window.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, [
    dispatch,
    isChatOpen,
    isMinimized,
    viewMode,
    ffAiChat2,
    AIEnabled,
    completedOnboarding,
    setViewMode,
    requestOpenChat,
  ]);

  const toggleOpen = () => {
    tracker.track(trackingEvents.AIChatClick, { variant: 'floating_launcher' });
    if (isMinimized) {
      requestOpenChat(() => dispatch(openChat()));
      return;
    }
    if (viewMode !== 'floating') {
      // If docked or expanded, just toggle open state
      if (isChatOpen) {
        dispatch(closeAIConversation());
      } else {
        requestOpenChat(() => dispatch(openChat()));
      }
      return;
    }
    if (isChatOpen) {
      dispatch(closeAIConversation());
    } else {
      requestOpenChat(() => dispatch(openChat()));
    }
  };

  const handleClose = () => {
    dispatch(closeAIConversation());
  };

  const handleViewModeChange = (mode: ChatViewMode) => {
    setViewMode(mode);
    if (mode !== 'floating') {
      requestOpenChat(() => dispatch(openChat()));
    }
  };

  if (ffAiChat2 && AIEnabled && completedOnboarding) {
    const isMinimizedState = isMinimized;
    return (
      <Stack gap={1} sx={launcherStyles} id='ai-chat-launcher-container'>
        <MinimizeWrapper
          isMinimized={isMinimizedState}
          onClick={
            isMinimizedState
              ? () => {
                  requestOpenChat(() => dispatch(openChat()));
                }
              : undefined
          }
          sx={isMinimizedState ? { cursor: 'pointer' } : {}}
        >
          <Collapse
            in={isMinimizedState}
            orientation='horizontal'
            timeout={400}
            sx={{
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              minWidth: 0,
            }}
          >
            <MinimizedChatTitle id='ai-chat-resume'>
              <Typography
                variant='paragraphMedium'
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {chatTitle}
              </Typography>
            </MinimizedChatTitle>
          </Collapse>
          <Tooltip title={`Ask Quicken AI (${cmdKey}${shiftKey}O)`} placement='left'>
            <Fab
              id='ai-chat-launcher'
              ref={anchorEl}
              onClick={toggleOpen}
              size='small'
              aria-label='ai-chat'
              sx={(theme) => ({
                background: theme.palette.ai.gradientRadial,
                color: theme.palette.common.white,
                transition: 'transform 0.2s ease-in-out',
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                  transform: 'scale(0.90)',
                  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                },
                '& svg': {
                  width: AI_LOGO_SIMPLE_GLYPH_SIZE,
                  height: AI_LOGO_SIMPLE_GLYPH_SIZE,
                  fill: theme.palette.common.white,
                },
              })}
            >
              <AILogoSimpleIcon />
            </Fab>
          </Tooltip>
        </MinimizeWrapper>
        <DraggablePopper
          isMinimized={isMinimized}
          viewMode={viewMode}
          anchorEl={anchorEl.current}
          open={open}
          placement='top'
          dragHandleRef={chatDragHandleRef}
        >
          <AIChat
            handleMinimize={() => dispatch(minimizeChat())}
            onViewModeChange={handleViewModeChange}
            viewMode={viewMode}
            onClose={handleClose}
            dragHandleRef={chatDragHandleRef}
            greeting={greeting}
          />
        </DraggablePopper>
      </Stack>
    );
  }
  if (isDev && ffAiChat) {
    return (
      <>
        <SimplifiAiAssistant
          visible={showAiAssistant}
          onClose={() => {
            setShowAiAssistant(false);
          }}
        />
        <Fab
          id='ai-chat-launcher-legacy'
          color='primary'
          aria-label='chat'
          onClick={() => {
            setShowAiAssistant(!showAiAssistant);
          }}
          sx={launcherStyles}
        >
          <ChatIcon />
        </Fab>
      </>
    );
  }
  return null;
};

const ChatLauncher = (props: ChatLauncherProps): JSX.Element => {
  return (
    <ChatSessionProvider>
      <ChatLauncherContent {...props} />
    </ChatSessionProvider>
  );
};

export default ChatLauncher;
export { ChatDockProvider, useChatDock } from './ChatDockContext';
